import { uniq } from 'lodash-es';

import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { MwCompanyService } from '@core/auth';
import { Config, ModuleItem } from '@core/config';
import {
  MwAppConfigService,
  MwIconLoaderService,
  MW_APP_CONFIG,
} from '@core/services';

import { SidebarId } from '../../layout.constants';
import { MwSidebarService } from '../sidebar/sidebar.service';
import { MwSidebarComponent } from './../sidebar/sidebar.component';
import { MwAppModulesService } from './app-modules.services';

@Component({
  selector: 'mw-app-modules',
  templateUrl: 'app-modules.component.html',
  styleUrls: ['./app-modules.component.scss'],
})
export class MwAppModulesComponent implements OnInit, OnDestroy {
  modules: ModuleItem[] = [];
  activeModule = `CORE.MODULE.${this.config.environment.moduleName}`;

  private unsubscribeAll$ = new Subject<void>();
  private appModulesSidebar: MwSidebarComponent;

  constructor(
    private readonly sidebarService: MwSidebarService,
    private readonly configService: MwAppConfigService,
    private readonly iconLoaderService: MwIconLoaderService,
    private readonly service: MwAppModulesService,
    private readonly companyService: MwCompanyService,
    @Inject(MW_APP_CONFIG) private readonly config: Config
  ) {
    this.subscribeToConfig();
  }

  ngOnInit(): void {
    this.subscribeToAppModulesSidebar();
  }

  ngOnDestroy(): void {
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  onShow(): void {
    if (!this.appModulesSidebar) {
      return;
    }

    this.appModulesSidebar.toggleOpen();
  }

  onClose(): void {
    if (!this.appModulesSidebar) {
      return;
    }
    this.appModulesSidebar.close();
  }

  onClick(item: ModuleItem): void {
    if (item.title !== this.activeModule) {
      this.service.navigate(item);
    }
  }

  private onModulesChange(modules: ModuleItem[]): void {
    this.modules = modules;
    const svgIcons = this.modules
      .map((t) => t.svgIcon)
      .filter((svgIcon) => !!svgIcon);

    const uniqueSvgIcons = uniq(svgIcons);
    this.iconLoaderService.load(uniqueSvgIcons);

    this.filtrateByFeatureFlags(this.modules);
  }

  private filtrateByFeatureFlags(items: ModuleItem[]): void {
    const featureFlags = this.getFeatureFlags(items);
    const featureFlagsStates = this.getFeatureFlagsStates(featureFlags);

    combineLatest(featureFlagsStates).subscribe((states) => {
      this.modules = this.modules.filter(
        (t) =>
          !t.featureFlag ||
          states.find((f) => f.key === t.featureFlag && f.enabled)
      );
    });
  }

  private getFeatureFlagsStates(
    featureFlags: string[]
  ): Observable<{ key: string; enabled: boolean }>[] {
    return featureFlags.map((featureFlag) =>
      this.companyService.getFeatureFlagState(featureFlag).pipe(
        map((enabled) => ({
          key: featureFlag,
          enabled: enabled,
        }))
      )
    );
  }

  private getFeatureFlags(items: ModuleItem[]): string[] {
    return items
      .filter((t) => !!t.featureFlag)
      .reduce((prev, curr) => {
        if (prev.indexOf(curr.featureFlag) === -1) {
          prev.push(curr.featureFlag);
        }
        return prev;
      }, []);
  }

  private subscribeToAppModulesSidebar(): void {
    this.sidebarService
      .getSidebar(SidebarId.AppModules)
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((sidebar) => (this.appModulesSidebar = sidebar));
  }

  private subscribeToConfig(): void {
    this.configService
      .getConfig()
      .pipe(
        takeUntil(this.unsubscribeAll$),
        map((config) => config?.modules ?? []),
        distinctUntilChanged(
          (a, b) => a.map((x) => x.url).join() === b.map((x) => x.url).join()
        )
      )
      .subscribe((config) => this.onModulesChange(config));
  }
}
